import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { FC, ReactNode } from "react";

export interface ConfigAccordionProps {
  title: string;
  children: ReactNode;
  /** Open on first render. Design 7a draws "Account creation" expanded. */
  defaultExpanded?: boolean;
  /** Test hook — Playwright asserts by testId, never by copy (UI-09). */
  testId?: string;
}

/**
 * One collapsible section of the programme Configuration tab (design 7a).
 *
 * The tab used to render six sections flat, separated by a heading and a rule, in a single Grid.
 * That was already a long scroll and PRET-1901 added a seventh block to it, so the design makes each
 * section an accordion item — the coordinator opens the one they came for.
 *
 * MUI's Accordion rather than a hand-rolled one: @mui/material is already the repo's component
 * library, and there was no existing accordion or collapse component to reuse (checked).
 *
 * Styling notes, matching the design:
 *
 * - outlined card per item, 8px radius, no elevation and no default MUI divider line, so collapsed
 *   items read as a stack of cards rather than a single ruled block
 * - every item title is BOLD, open or closed. 7a draws them that way, and at regular weight they
 *   sat lighter than the field labels inside them — the section headings read as less important
 *   than the controls they contain
 * - the EXPANDED item takes the frame's own values: `#1A43D2` border and title, `#EEF3FE` header
 *   fill (theme `menu.dark` and `panel.light`). Both are read off 7a rather than derived — an
 *   earlier attempt used `alpha(primary.main, 0.08)`, and `primary.main` is `#355DE8` in this
 *   theme, a lighter blue, so the accent came out visibly different from the design. It replaced
 *   `action.hover`, the neutral grey the `panel` palette's own note warns against — on a
 *   blue-accented form that grey reads as disabled rather than open
 * - `disableGutters` because the surrounding modal already owns the spacing
 *
 * Children are `<Grid item>` elements — the same ones the flat layout used — so this supplies the
 * `<Grid container>` they need. That is what keeps the section bodies unchanged.
 */
const ConfigAccordion: FC<ConfigAccordionProps> = ({
  title,
  children,
  defaultExpanded = false,
  testId,
}) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    data-testid={testId}
    sx={{
      width: "100%",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "8px",
      mb: "12px",
      "&:before": { display: "none" },
      "&.Mui-expanded": {
        borderColor: "menu.dark",
      },
      "& .MuiAccordionSummary-root.Mui-expanded": {
        backgroundColor: "panel.light",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
      },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls={`${testId ?? title}-content`}
      id={`${testId ?? title}-header`}
      sx={{
        minHeight: "64px",
        px: "24px",
        "& .MuiAccordionSummary-content": { my: "12px" },
        // No fontSize override. `titleSmall` is already the 18px the frame specifies, so the
        // variant carries the size and only the COLOUR is applied on top — which is what the
        // developer asked for after a first pass had dropped it to 16px.
        "& .MuiTypography-root": {
          fontWeight: 700,
        },
        "&.Mui-expanded .MuiTypography-root": {
          color: "menu.dark",
        },
      }}
    >
      <Typography variant="titleSmall" component="div">
        {title}
      </Typography>
    </AccordionSummary>

    {/*
      pt is 20px, not 0. MUI's AccordionDetails defaults to no top padding, and the first control in
      a section is an outlined TextField whose floating LABEL sits above its own border — with no
      padding that label collides with the summary's bottom edge and reads as overlapping it.
      20px clears the label; the 24px sides match the summary so the body lines up with the title.
    */}
    <AccordionDetails sx={{ px: "24px", pt: "20px", pb: "24px" }}>
      <Grid container spacing={"16px"}>
        {children}
      </Grid>
    </AccordionDetails>
  </Accordion>
);

export default ConfigAccordion;
